/**
 * Composes the X / Y / X2 axis dropzones plus the auxiliary controls
 * (stats type, date interval, paging count) that live alongside them.
 *
 * Mirrors the legacy `EditReportMain.aspx` "Design" panel.
 */

import { useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Label } from '../../../components/ui/label';
import {
  type BuilderFieldDef,
  type DsidCatalogResponse,
  type FieldKind,
  type IndicatorBuilderConfig,
} from '../../../lib/api/reporting';
import { AxisDropzone } from './AxisDropzone';

const X_ACCEPTED_KINDS: readonly FieldKind[] = ['group_key', 'date_bucket', 'enum', 'value'];
const Y_ACCEPTED_KINDS: readonly FieldKind[] = ['value'];
const X2_ACCEPTED_KINDS: readonly FieldKind[] = ['group_key', 'enum'];

const KIND_LABELS: Record<FieldKind, string> = {
  group_key: 'identifier',
  value: 'numeric',
  date_bucket: 'date',
  enum: 'category',
};

function showAxisRejection(axisLabel: string, field: BuilderFieldDef, accepted: readonly FieldKind[]) {
  const allow = accepted.map((k) => KIND_LABELS[k]).join(' / ');
  toast.error(
    `${axisLabel} only accepts ${allow} fields. "${field.label}" is ${KIND_LABELS[field.kind]}.`,
  );
}

/** Stats type options matching the backend `chart_resolver/stats.py` map. */
export const STATS_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'Sum' },
  { value: 1, label: 'Count' },
  { value: 2, label: 'Average' },
  { value: 3, label: 'Max' },
  { value: 4, label: 'Min' },
  { value: 5, label: 'Percentage' },
  { value: 6, label: 'Distinct Count' },
  { value: 7, label: 'History (Running Total)' },
];

/**
 * Only a subset of stats types makes sense for a given chart / X combination.
 * Hide the inapplicable ones so users don't pick e.g. "History (Running Total)"
 * on a customer-grouped bar chart (which turns every bar into an ever-rising
 * cumulative, visually misleading).
 *
 *   - History needs a *time series* X (date_bucket) to be meaningful.
 *   - KPI / Indicator render a single number; Percentage/History/Min/Max are
 *     usually not what the user wants.
 *   - Grid ignores stats_type when no aggregate is configured, so any choice
 *     is fine there.
 */
function getApplicableStatsTypes(
  chartType: string | undefined,
  xKind: FieldKind | undefined,
): { value: number; label: string }[] {
  const hideHistory = xKind !== 'date_bucket';
  const kpiLike = chartType === 'kpi' || chartType === 'indicator';

  return STATS_TYPES.filter((s) => {
    if (s.value === 7 && hideHistory) return false;           // History needs time-series X
    if (kpiLike && (s.value === 5 || s.value === 7)) return false;  // Percentage/History don't fit a single tile
    return true;
  });
}

/** Date interval options matching `chart_resolver/stats.py` DATE_TRUNC. */
export const DATE_INTERVALS: { value: number; label: string }[] = [
  { value: 0, label: 'Day' },
  { value: 1, label: 'Week' },
  { value: 2, label: 'Month' },
  { value: 3, label: 'Quarter' },
  { value: 4, label: 'Year' },
  { value: 10, label: 'Fiscal Month' },
  { value: 11, label: 'Fiscal Quarter' },
  { value: 12, label: 'Fiscal Year' },
];

/** Legacy EACReportXAxisPagingCounts presets. */
export const PAGING_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: 'All' },
  { value: 4, label: 'Top 4' },
  { value: 5, label: 'Top 5' },
  { value: 6, label: 'Top 6' },
  { value: 7, label: 'Top 7' },
  { value: 12, label: 'Top 12' },
  { value: 26, label: 'Top 26' },
  { value: 52, label: 'Top 52' },
  { value: 365, label: 'Top 365' },
];

interface AxisDesignPanelProps {
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  catalog: DsidCatalogResponse | null;
  /** Drives which Stats Type options are surfaced (e.g. History only for time-series X). */
  chartType?: string;
}

/** Look up a BuilderFieldDef by (dsid, field_id). */
export function lookupField(
  catalog: DsidCatalogResponse | null,
  dsid: number | undefined,
  fieldId: number | undefined,
): BuilderFieldDef | null {
  if (!catalog || dsid === undefined || fieldId === undefined) return null;
  for (const ds of catalog.datasets) {
    if (ds.dsid !== dsid) continue;
    for (const f of ds.fields) {
      if (f.field_id === fieldId) return f;
    }
  }
  return null;
}

export function AxisDesignPanel({ config, onChange, catalog, chartType }: AxisDesignPanelProps) {
  const xField = lookupField(catalog, config.x?.dsid, config.x?.field_id);
  const yField = lookupField(catalog, config.y?.dsid, config.y?.field_id);
  const x2Field = lookupField(catalog, config.x2?.dsid, config.x2?.field_id);

  const isDateBucket = xField?.kind === 'date_bucket';
  const statsValue = config.y?.stats_type ?? 1;

  // Filter inappropriate options out of the Stats Type dropdown so users don't
  // accidentally pick ones that produce nonsense for the current X/chart combo.
  const applicableStats = useMemo(
    () => getApplicableStatsTypes(chartType, xField?.kind),
    [chartType, xField?.kind],
  );
  const isApplicable = applicableStats.some((s) => s.value === statsValue);
  // If the currently-saved stats_type isn't applicable, surface it anyway so
  // the user can see and change it — otherwise the select would appear empty.
  const statsOptions = useMemo(() => {
    if (isApplicable) return applicableStats;
    const legacy = STATS_TYPES.find((s) => s.value === statsValue);
    if (!legacy) return applicableStats;
    return [...applicableStats, { value: legacy.value, label: `${legacy.label} (not recommended here)` }];
  }, [applicableStats, statsValue, isApplicable]);

  // Auto-normalise a legacy stats_type that no longer fits the current X/chart
  // combo. We only fire once per (chartType, xKind) change so we don't fight
  // the user if they deliberately pick the "(not recommended)" option back.
  const normalizeSignature = useRef<string | null>(null);
  useEffect(() => {
    if (!config.y) return;
    const sig = `${chartType || ''}|${xField?.kind || ''}`;
    if (normalizeSignature.current === sig) return;
    normalizeSignature.current = sig;
    if (isApplicable) return;
    // Reset to Sum (0) — the least-surprising default aggregator.
    onChange({ ...config, y: { ...config.y, stats_type: 0 } });
    const legacy = STATS_TYPES.find((s) => s.value === statsValue);
    toast.info(
      `Stats Type reset to Sum — "${legacy?.label || 'previous choice'}" doesn't apply to this chart.`,
    );
  }, [chartType, xField?.kind, isApplicable, config, statsValue, onChange]);
  const dateInterval = config.x?.date_interval ?? 2;
  const pagingCount = config.x?.paging_count ?? 10;

  const setX = (field: BuilderFieldDef | null) => {
    if (!field) {
      onChange({ ...config, x: null });
      return;
    }
    const prev = config.x;
    onChange({
      ...config,
      x: {
        dsid: field.dsid,
        field_id: field.field_id,
        date_interval: prev?.date_interval ?? null,
        paging_count: prev?.paging_count ?? null,
      },
    });
  };

  const setY = (field: BuilderFieldDef | null) => {
    if (!field) {
      onChange({ ...config, y: null });
      return;
    }
    const prev = config.y;
    onChange({
      ...config,
      y: {
        dsid: field.dsid,
        field_id: field.field_id,
        stats_type: prev?.stats_type ?? 1,
      },
    });
  };

  const setX2 = (field: BuilderFieldDef | null) => {
    if (!field) {
      onChange({ ...config, x2: null });
      return;
    }
    onChange({
      ...config,
      x2: {
        dsid: field.dsid,
        field_id: field.field_id,
      },
    });
  };

  const setStats = (statsType: number) => {
    if (!config.y) return;
    onChange({ ...config, y: { ...config.y, stats_type: statsType } });
  };

  const setDateInterval = (interval: number) => {
    if (!config.x) return;
    onChange({ ...config, x: { ...config.x, date_interval: interval } });
  };

  const setPagingCount = (count: number) => {
    const x = config.x ?? null;
    if (x) {
      onChange({ ...config, x: { ...x, paging_count: count } });
    } else {
      onChange({ ...config, paging_count: count });
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <AxisDropzone
          axis="x"
          label="X Axis (Group)"
          value={xField}
          onChange={setX}
          onClear={() => setX(null)}
          acceptedKinds={X_ACCEPTED_KINDS}
          onReject={(f, accepted) => showAxisRejection('X axis', f, accepted)}
        />
        <AxisDropzone
          axis="y"
          label="Y Axis (Value)"
          value={yField}
          onChange={setY}
          onClear={() => setY(null)}
          acceptedKinds={Y_ACCEPTED_KINDS}
          onReject={(f, accepted) => showAxisRejection('Y axis', f, accepted)}
          helper="Numeric fields only (use Count/Distinct on a grouping field instead)"
        />
        <AxisDropzone
          axis="x2"
          label="X2 (Series Split)"
          value={x2Field}
          onChange={setX2}
          onClear={() => setX2(null)}
          acceptedKinds={X2_ACCEPTED_KINDS}
          onReject={(f, accepted) => showAxisRejection('X2', f, accepted)}
          helper="Optional secondary grouping (category only)"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Stats Type</Label>
          <Select
            value={String(statsValue)}
            onValueChange={(v) => setStats(parseInt(v, 10))}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Stats type" />
            </SelectTrigger>
            <SelectContent>
              {statsOptions.map((s) => (
                <SelectItem key={s.value} value={String(s.value)}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isDateBucket && (
          <div className="space-y-1">
            <Label className="text-xs">Date Interval</Label>
            <Select
              value={String(dateInterval)}
              onValueChange={(v) => setDateInterval(parseInt(v, 10))}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Date interval" />
              </SelectTrigger>
              <SelectContent>
                {DATE_INTERVALS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Paging Count</Label>
          <Select
            value={String(pagingCount)}
            onValueChange={(v) => {
              if (v === 'custom') return;
              setPagingCount(parseInt(v, 10));
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Paging" />
            </SelectTrigger>
            <SelectContent>
              {PAGING_PRESETS.map((p) => (
                <SelectItem key={p.value} value={String(p.value)}>
                  {p.label}
                </SelectItem>
              ))}
              {!PAGING_PRESETS.some((p) => p.value === pagingCount) && (
                <SelectItem value={String(pagingCount)}>
                  Custom ({pagingCount})
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
